// client/src/components/paymentUploads/PaymentUploadDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import paymentUploadService from '../../services/paymentUploadService';

const PaymentUploadDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [paymentUpload, setPaymentUpload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Load payment upload data
  useEffect(() => {
    const loadPaymentUpload = async () => {
      try {
        setLoading(true);
        const response = await paymentUploadService.getPaymentUpload(id);
        setPaymentUpload(response.data);
        setError('');
      } catch (err) {
        setError(t('errorLoadingPaymentUploadDetails'));
      } finally {
        setLoading(false);
      }
    };
    
    loadPaymentUpload();
  }, [id, t]);
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value) return formatCurrency(0);
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(parseFloat(value));
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'processed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Calculate progress percentage
  const calculateProgress = () => {
    if (!paymentUpload) return 0;
    
    return (paymentUpload.matched_records / paymentUpload.total_records) * 100;
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payment-uploads')}
        >
          {t('back')}
        </Button>
      </Container>
    );
  }
  
  if (!paymentUpload) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">{t('paymentUploadNotFound')}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/payment-uploads')}
          >
            {t('back')}
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('paymentUploadDetails')}
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            {t('uploadID')}: {paymentUpload.id}
          </Typography>
          
          <Chip 
            label={t(paymentUpload.status)}
            color={getStatusColor(paymentUpload.status)}
            size="medium"
          />
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('fileName')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {paymentUpload.file_name}
            </Typography>
            
            <Typography variant="subtitle1">{t('uploadedBy')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {paymentUpload.creator?.username || '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('uploadDate')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatDate(paymentUpload.created_at)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('totalRecords')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {paymentUpload.total_records}
            </Typography>
            
            <Typography variant="subtitle1">{t('matchedRecords')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {paymentUpload.matched_records}
            </Typography>
            
            <Typography variant="subtitle1">{t('processingProgress')}:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateProgress()} 
                  color={paymentUpload.matched_records === paymentUpload.total_records ? "success" : "primary"}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">
                  {`${Math.round(calculateProgress())}%`}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        {paymentUpload.comments && (
          <Alert 
            severity={paymentUpload.status === 'failed' ? "error" : "info"} 
            sx={{ mt: 2 }}
          >
            <Typography variant="subtitle2">{t('comments')}:</Typography>
            <Typography variant="body2">{paymentUpload.comments}</Typography>
          </Alert>
        )}
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          {t('paymentItems')}
        </Typography>
        
        {paymentUpload.items && paymentUpload.items.length > 0 ? (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="payment items table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('nationalID')}</TableCell>
                    <TableCell>{t('nameEN')}</TableCell>
                    <TableCell>{t('nameAR')}</TableCell>
                    <TableCell>{t('paymentDate')}</TableCell>
                    <TableCell>{t('paymentReference')}</TableCell>
                    <TableCell align="right">{t('totalValue')}</TableCell>
                    <TableCell align="center">{t('status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentUpload.items
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item) => (
                      <TableRow 
                        key={item.id} 
                        hover
                        sx={{
                          backgroundColor: item.status === 'failed' ? 'error.light' : 'inherit'
                        }}
                      >
                        <TableCell>{item.national_id}</TableCell>
                        <TableCell>{item.name_en}</TableCell>
                        <TableCell>{item.name_ar}</TableCell>
                        <TableCell>{formatDate(item.payment_date)}</TableCell>
                        <TableCell>{item.payment_reference}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total_value)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={item.error_message || ''}>
                            <Chip 
                              icon={item.status === 'processed' ? <CheckIcon /> : <ErrorIcon />}
                              label={t(item.status)}
                              color={getStatusColor(item.status)}
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={paymentUpload.items.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('rowsPerPage')}
            />
          </Paper>
        ) : (
          <Alert severity="info">
            {t('noItemsFound')}
          </Alert>
        )}
        
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/payment-uploads')}
          >
            {t('backToUploads')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentUploadDetails;